import type { RealtimeEvent } from "@shared/types/foyer";

interface ConnectArgs {
  premiseId: string;
  roomId?: string;
  /** Subscribe to the screen-scoped group too (used for targeted
   * actions like reboot, unpair, or per-device messages). */
  screenId?: string;
  /**
   * Screen-token issued at pairing. Required by the server - without it
   * both the negotiate and SSE endpoints return 401.
   */
  screenToken?: string;
  apiBase?: string;
  /** Called for every event received on the stream. */
  onEvent: (evt: RealtimeEvent) => void;
  /** Called when the connection opens. */
  onOpen?: () => void;
  /** Called when the connection drops; the client will auto-reconnect. */
  onError?: () => void;
}

/**
 * Open a realtime connection for the given premise and pump events to
 * `onEvent`. Returns a synchronous close() handle.
 *
 * Transport selection (decided per-connection):
 *   1. **Azure Web PubSub** - preferred. The server's negotiate endpoint hands
 *      back a client-access URL whose groups are pinned to this screen. This is
 *      the multi-instance-safe path: the service delivers an event to this TV
 *      no matter which API instance published it.
 *   2. **SSE** - fallback when Web PubSub isn't configured (local dev) or the
 *      negotiate call fails. In-process only, so correct on a single instance.
 */
export function connectRealtime(args: ConnectArgs): () => void {
  let cancelled = false;
  let cleanup: () => void = () => {};

  void (async () => {
    const url = args.screenToken ? await tryNegotiate(args) : null;
    if (cancelled) return;
    if (url) {
      try {
        const handle = await connectWebPubSub(args, url);
        if (cancelled) handle();
        else cleanup = handle;
        return;
      } catch {
        if (cancelled) return;
        // Web PubSub failed to start - fall back to SSE below.
      }
    }
    if (cancelled) return;
    cleanup = connectSse(args);
  })();

  return () => {
    cancelled = true;
    cleanup();
  };
}

/** Ask the server for a Web PubSub client URL. Returns null if Web PubSub
 *  isn't available (503) or the request fails - caller falls back to SSE. */
async function tryNegotiate(args: ConnectArgs): Promise<string | null> {
  if (typeof fetch !== "function") return null;
  try {
    const res = await fetch(negotiateUrl(args));
    if (!res.ok) return null;
    const body = (await res.json()) as { url?: string };
    return body.url ?? null;
  } catch {
    return null;
  }
}

function negotiateUrl(args: ConnectArgs): string {
  const params = new URLSearchParams({ premise: args.premiseId });
  if (args.screenToken) params.set("screen_token", args.screenToken);
  return `${args.apiBase ?? ""}/api/common/v1/realtime/negotiate?${params.toString()}`;
}

/** Connect via Azure Web PubSub. The SDK is lazy-imported so it's only loaded
 *  (and bundled as a separate chunk) when this transport is actually used. */
async function connectWebPubSub(
  args: ConnectArgs,
  initialUrl: string
): Promise<() => void> {
  const { WebPubSubClient } = await import("@azure/web-pubsub-client");

  let first = true;
  const client = new WebPubSubClient({
    // First connect reuses the URL we already negotiated; reconnects re-mint a
    // fresh one so a long-lived TV survives token expiry.
    getClientAccessUrl: async () => {
      if (first) {
        first = false;
        return initialUrl;
      }
      const url = await tryNegotiate(args);
      if (!url) throw new Error("realtime negotiate failed on reconnect");
      return url;
    },
  });

  client.on("group-message", (e) => {
    try {
      const data = e.message.data as unknown;
      const evt = (
        typeof data === "string" ? JSON.parse(data) : data
      ) as RealtimeEvent;
      args.onEvent(evt);
    } catch {
      // Ignore malformed payloads - the connection itself is still good.
    }
  });
  client.on("connected", () => args.onOpen?.());
  client.on("disconnected", () => args.onError?.());

  await client.start();
  return () => client.stop();
}

/**
 * SSE transport (fallback). Opens an EventSource to the Foyer server.
 * EventSource handles reconnect automatically on transient errors.
 * Exported for tests and as the explicit single-instance transport.
 */
export function connectSse({
  premiseId,
  roomId,
  screenId,
  screenToken,
  apiBase = "",
  onEvent,
  onOpen,
  onError,
}: ConnectArgs): () => void {
  // Environments without EventSource (e.g. jsdom in unit tests) get a noop.
  if (typeof EventSource === "undefined") {
    return () => {};
  }

  const params = new URLSearchParams({ premise: premiseId });
  if (roomId) params.set("room", roomId);
  if (screenId) params.set("screen", screenId);
  if (screenToken) params.set("screen_token", screenToken);
  const url = `${apiBase}/api/common/v1/events?${params.toString()}`;

  const es = new EventSource(url);

  es.addEventListener("open", () => onOpen?.());

  es.addEventListener("message", (e) => {
    try {
      const evt = JSON.parse((e as MessageEvent<string>).data) as RealtimeEvent;
      onEvent(evt);
    } catch {
      // Ignore malformed payloads - the connection itself is still good.
    }
  });

  es.addEventListener("error", () => onError?.());

  return () => es.close();
}
